import { motion } from 'framer-motion';
import { ArrowRight, Shield, Code, Network } from 'lucide-react';
import logo from '../logo.png';

const floatingIcons = [
  { icon: Shield, x: '10%', y: '20%', delay: 0 },
  { icon: Code,   x: '85%', y: '15%', delay: 0.4 },
  { icon: Network,x: '90%', y: '70%', delay: 0.8 },
];

export default function Hero() {
  return (
    <section style={{ position: 'relative', minHeight: '100vh', display: 'flex', alignItems: 'center', overflow: 'hidden', background: 'linear-gradient(180deg, #ffffff 0%, #f4fbfc 100%)' }}>

      {/* Animated grid bg */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 0,
        backgroundImage: `
          linear-gradient(rgba(0,172,193,0.08) 1px, transparent 1px),
          linear-gradient(90deg, rgba(0,172,193,0.08) 1px, transparent 1px)
        `,
        backgroundSize: '60px 60px',
      }} />

      {/* Glow blobs */}
      <div style={{ position: 'absolute', top: '15%', left: '5%', width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(0,172,193,0.12) 0%, transparent 70%)', zIndex: 0 }} />
      <div style={{ position: 'absolute', bottom: '10%', right: '8%', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(217,122,0,0.1) 0%, transparent 70%)', zIndex: 0 }} />

      {/* Floating icons */}
      {floatingIcons.map(({ icon: Icon, x, y, delay }, i) => (
        <motion.div key={i}
          style={{ position: 'absolute', left: x, top: y, zIndex: 1, opacity: 0.18 }}
          animate={{ y: [0, -18, 0] }}
          transition={{ duration: 4 + i, repeat: Infinity, delay, ease: 'easeInOut' }}>
          <Icon size={48} color="var(--teal)" />
        </motion.div>
      ))}

      {/* Diagonal accent bar */}
      <div style={{
        position: 'absolute', right: 0, top: 0, bottom: 0, width: '38%', zIndex: 0,
        background: 'linear-gradient(135deg, transparent 0%, rgba(0,109,123,0.08) 100%)',
        clipPath: 'polygon(20% 0%, 100% 0%, 100% 100%, 0% 100%)',
      }} />

      <div style={{ position: 'relative', zIndex: 2, maxWidth: 1280, margin: '0 auto', padding: '0 5%', paddingTop: 100, width: '100%' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60, alignItems: 'center' }} className="hero-grid">

          {/* Left: text */}
          <div>
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.2 }}>
              <span style={{
                fontFamily: 'Space Mono', fontSize: 11, letterSpacing: '0.25em', textTransform: 'uppercase',
                color: 'var(--teal-dark)', background: 'rgba(0,172,193,0.12)', border: '1px solid rgba(0,172,193,0.22)',
                padding: '6px 14px', borderRadius: 2, display: 'inline-block', marginBottom: 28,
              }}>IT Consulting · Cyber Security · Software</span>
            </motion.div>

            <motion.h1 initial={{ opacity: 0, x: -40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.35 }}
              style={{ fontSize: 'clamp(42px, 5.5vw, 76px)', fontWeight: 700, lineHeight: 1.05, marginBottom: 24, color: 'var(--text-main)' }}>
              Where <span style={{ color: 'var(--teal-dark)' }}>Reliability</span>{' '}
              <br />Comes First.
            </motion.h1>

            <motion.p initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.5 }}
              style={{ fontSize: 18, lineHeight: 1.7, color: 'var(--text-muted)', maxWidth: 520, marginBottom: 44, fontWeight: 300 }}>
              End-to-end technology consulting that secures, builds, and optimises your business — delivered with precision and a commitment to trust.
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.65 }}
              style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <a href="#services"
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  background: 'linear-gradient(135deg, var(--teal), var(--teal-dark))',
                  color: '#fff', fontFamily: 'Rajdhani', fontWeight: 700, fontSize: 15,
                  letterSpacing: '0.08em', textTransform: 'uppercase', padding: '14px 30px',
                  borderRadius: 4, transition: 'transform 0.2s, box-shadow 0.2s',
                  boxShadow: '0 16px 36px rgba(0,172,193,0.24)',
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 20px 44px rgba(0,172,193,0.32)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 16px 36px rgba(0,172,193,0.24)'; }}>
                Explore Services <ArrowRight size={16} />
              </a>
              <a href="https://wa.me/919988778877" target="_blank" rel="noreferrer"
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  border: '1px solid rgba(0,172,193,0.28)', color: 'var(--teal-dark)', background: '#ffffff',
                  fontFamily: 'Rajdhani', fontWeight: 700, fontSize: 15,
                  letterSpacing: '0.08em', textTransform: 'uppercase', padding: '14px 30px',
                  borderRadius: 4, transition: 'background 0.2s, border-color 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,172,193,0.08)'; e.currentTarget.style.borderColor = 'var(--teal)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#ffffff'; e.currentTarget.style.borderColor = 'rgba(0,172,193,0.28)'; }}>
                Talk to Us
              </a>
            </motion.div>

            {/* Stats row */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.9 }}
              style={{ display: 'flex', gap: 40, marginTop: 64, flexWrap: 'wrap' }}>
              {[['10+', 'Years Experience'], ['50+', 'Projects Delivered'], ['100%', 'Client Satisfaction']].map(([num, label]) => (
                <div key={label}>
                  <div style={{ fontFamily: 'Rajdhani', fontWeight: 700, fontSize: 32, color: 'var(--teal-dark)', lineHeight: 1 }}>{num}</div>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4, letterSpacing: '0.05em' }}>{label}</div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right: logo showcase */}
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, delay: 0.3 }}
            style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative' }}
            className="hero-logo-side">
            {/* Outer ring */}
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
              style={{
                width: 380, height: 380, borderRadius: '50%',
                border: '1px solid rgba(0,172,193,0.2)',
                position: 'absolute',
                background: 'conic-gradient(from 0deg, transparent 70%, rgba(0,172,193,0.18) 100%)',
              }} />
            {/* Inner ring */}
            <motion.div
              animate={{ rotate: -360 }}
              transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
              style={{
                width: 300, height: 300, borderRadius: '50%',
                border: '1px dashed rgba(217,122,0,0.25)',
                position: 'absolute',
              }} />
            <img src={logo} alt="KP Technologies" style={{ width: 240, height: 240, objectFit: 'contain', position: 'relative', zIndex: 2, filter: 'drop-shadow(0 24px 44px rgba(15,23,42,0.18))' }} />
          </motion.div>
        </div>
      </div>

      {/* Bottom fade */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 120, background: 'linear-gradient(to top, #ffffff, transparent)', zIndex: 3 }} />

      <style>{`
        @media (max-width: 900px) {
          .hero-grid { grid-template-columns: 1fr !important; }
          .hero-logo-side { display: none !important; }
        }
      `}</style>
    </section>
  );
}
